import React from 'react';
import { KeyRound } from 'lucide-react';
import { useQzTraySigner } from '../../../hooks/qztray/useQzTraySigner';
import SelectField from '../../Forms/Input/SelectField';
import TextAreaField from '../../Forms/Input/TextAreaField';
import ButtonCustom from '../../../pages/UiElements/ButtonCustom';
import SignatureResultBox from './SignatureResultBox';
import SignatureErrorBox from './SignatureErrorBox';
import SignaturePayloadBox from './SignaturePayloadBox';
import { useDispatch } from 'react-redux';
import { resetSignature } from '../../../store/slices/qztray/qzTraySignerSlice';

const SignatureTester: React.FC = () => {
  const dispatch = useDispatch();

  const {
    payload,
    payloadPretty,
    certificateType,
    signature,
    fileName,
    signedAt,
    loading,
    error,
    setPayload,
    setCertificateType,
    handleSign,
  } = useQzTraySigner();

  // 🔹 JSON ejemplo (solo prueba)
  const examplePayload = JSON.stringify(
    {
      saleCode: 'A001',
      controlNumber: 'CN-100045',
      total: 25.5,
      date: '2025-10-18T22:15:00',
    },
    null,
    2
  );

  React.useEffect(() => {
    if (!payload) setPayload(examplePayload);
  }, [payload]);

  // ✅ Ahora forzamos siempre PRIVATE_KEY (no se selecciona)
  React.useEffect(() => {
    if (certificateType !== "PRIVATE_KEY") {
      setCertificateType("PRIVATE_KEY");
    }
  }, [certificateType]);

  // 🧹 limpiar estado al desmontar
  React.useEffect(() => {
    return () => {
      dispatch(resetSignature());
    };
  }, []);

  return (
    <div className="w-full rounded-2xl bg-white p-8 shadow-xl dark:bg-boxdark">

      {/* 🧾 Título */}
      <div className="mb-6 border-b border-gray-200 pb-4 dark:border-gray-700">
        <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-800 dark:text-white">
          <KeyRound className="h-6 w-6 text-primary" />
          Probar Firma Digital QZ Tray
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Esta herramienta te permite verificar la firma digital usando la clave privada configurada.
        </p>
      </div>

      {/* ⚙️ Formulario */}
      <div className="grid w-full grid-cols-1 gap-6">

        {/* 🔐 Tipo fijo */}
        <SelectField
          label="Tipo de Certificado"
          name="certificateType"
          value={certificateType}
          onChange={() => {}}   // ← deshabilitado porque es fijo
          disabled={true}
          options={[
            { label: 'Clave Privada (PRIVATE_KEY)', value: 'PRIVATE_KEY' },
          ]}
          iconKey="key"
        />

        {/* 📝 Payload JSON */}
        <TextAreaField
          label="Payload a firmar (JSON)"
          name="payload"
          value={payloadPretty || examplePayload}
          onChange={() => {}}
          rows={10}
          disabled={true}
          iconKey="file-text"
        />
      </div>

      {/* 🔘 Acción */}
      <div className="mt-8 flex justify-end">
        <ButtonCustom
          text={loading ? 'Firmando...' : 'Firmar Payload'}
          onClick={handleSign}
          disabled={loading}
          variant="primary"
          size="md"
          rounded="md"
        />
      </div>

      {/* 📦 Mostrar payload firmado */}
      {signature && (
        <SignaturePayloadBox
          payload={payload}
          payloadPretty={payloadPretty}
        />
      )}

      {/* ✅ Resultado */}
      {signature && (
        <SignatureResultBox
          signature={signature}
          fileName={fileName}
          signedAt={signedAt}
        />
      )}

      {/* ⚠️ Error */}
      {error && <SignatureErrorBox message={error} />}
    </div>
  );
};

export default SignatureTester;
